import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { EditTeamMemberProfileContent } from './content'
import { EditTeamMemberProfileControls } from './controls'

export enum Input {
  FirstName = 'first_name',
  LastName = 'last_name',
}

export const editTeamMemberProfileSchema = z.object({
  [Input.FirstName]: z.string()
    .min(1, { message: 'First name is required.' })
    .max(255, { message: 'First name must be less than 255 characters.' }),
  [Input.LastName]: z.string()
    .min(1, { message: 'Last name is required.' })
    .max(255, { message: 'Last name must be less than 255 characters.' }),
})

export type EditTeamMemberProfileSchema = z.infer<typeof editTeamMemberProfileSchema>

type EditTeamMemberProfileFormContextType = {
  onSubmit: (values: EditTeamMemberProfileSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const EditTeamMemberProfileFormContext = createContext<EditTeamMemberProfileFormContextType | undefined>(undefined)

type EditTeamMemberProfileFormProps = EditTeamMemberProfileFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditTeamMemberProfileSchema>,
}

export function EditTeamMemberProfileForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditTeamMemberProfileFormProps) {

  const context: EditTeamMemberProfileFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditTeamMemberProfileFormContext.Provider value={context}>
      <Form<EditTeamMemberProfileSchema, typeof editTeamMemberProfileSchema>
        schema={editTeamMemberProfileSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditTeamMemberProfileFormContext.Provider>
  )
}

export function useEditTeamMemberProfileForm() {
  const context = useContext(EditTeamMemberProfileFormContext)
  if (context === undefined) {
    throw new Error('useEditTeamMemberProfileForm must be used within an EditTeamMemberProfileFormProvider')
  }
  return context
}

// Compound components
EditTeamMemberProfileForm.Content = EditTeamMemberProfileContent
EditTeamMemberProfileForm.Controls = EditTeamMemberProfileControls
